import React, { useState } from 'react'
import ReportLayout from '../../layouts/Report'

import useGetSchools from '../../../hooks/schools/useGetSchools'
import useGetCampuses from '../../../hooks/campuses/useGetCampuses'
import useGetCertificateAwardsReport from '../../../hooks/analytics/useGetCertificateAwardsReport'
import useExportCertificateAwardsReport from '../../../hooks/analytics/useExportCertificateAwardsReport'
import { useGetStudentOnboardingResponsesOptionsByField } from '../../../hooks/misc'

import ReportTablet from '../../sub_components/ReportTablet'
import ReportDatePicker from '../../sub_components/ReportDatePicker'
import FilterTable from '../../sub_components/FilterTable'
import BarChart from '../../sub_components/charts/BarChart'

import { StudentOnboardingResponseOptionsField } from '../../../interfaces/types'
import { IColumnProps, IFilterableDataProps } from '../../../interfaces/table'

import { getDateString } from '../../../helpers/utility'


const CertificateAwards = () => {
    const [schoolFilters, setSchoolFilters] = useState<number[]>([]);
    const [campusFilters, setCampusFilters] = useState<number[]>([]);
    const [programsOfStudyFilters, setProgramsOfStudyFilters] = useState<number[]>([]);
    const [startDate, setStartDate] = useState<string>("");
    const [endDate, setEndDate] = useState<string>("");

    const { data: programsOfStudyOption } = useGetStudentOnboardingResponsesOptionsByField(StudentOnboardingResponseOptionsField.PROGRAM_OF_STUDY);

    const programsOfStudyOptions = programsOfStudyOption?.map((program, index) => ({
        id: index,
        name: program,
    })) ?? [];

    const filterQuery = new URLSearchParams();
    filterQuery.set("start_date", startDate);
    filterQuery.set("end_date", endDate);
    schoolFilters.forEach((id) => filterQuery.append("school_ids", id.toString()));
    campusFilters.forEach((id) => filterQuery.append("campus_ids", id.toString()));
    programsOfStudyFilters.forEach((id) => filterQuery.append("programs_of_study", programsOfStudyOptions[id].name));

    const { data: schools } = useGetSchools();
    const { data: campuses } = useGetCampuses();
    const { data: certificateAwardsReport } = useGetCertificateAwardsReport(filterQuery.toString());
    const { mutate: exportCertificateAwardsReport } = useExportCertificateAwardsReport();


    let filterTableDataColumns__Schools: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "School Name", identifier: "name" },
    ];
    let filterTableDataColumns__Campuses: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Campus Name", identifier: "name" },
    ];
    let filterTableDataColumns__ProgramsOfStudy: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Program of Study", identifier: "name" },
    ];

    let filterTableDataColumns__CertificateAwardsReport: IColumnProps[] = [
        { name: "ID", identifier: "id" },
        { name: "Full Name", identifier: "full_name" },
        { name: "Email", identifier: "email" },
        { name: "School", identifier: "school_name" },
        { name: "Course", identifier: "course_name" },
        { name: "Status", identifier: "status" },
        { name: "Completion Date", identifier: "completed_at" },
    ];

    let filterTableData__Schools: IFilterableDataProps[] = [];
    let filterTableData__Campuses: IFilterableDataProps[] = [];
    let filterTableData__ProgramsOfStudy: IFilterableDataProps[] = [];
    let filterTableData__CertificateAwardsReport: IFilterableDataProps[] = [];

    if (schools) {
        filterTableData__Schools = schools.map((school) => ({
            id: school.id,
            name: school.name,
        }));
    }

    if (campuses) {
        filterTableData__Campuses = campuses.map((campus) => ({
            id: campus.id,
            name: campus.name,
        }));
    }

    if (programsOfStudyOptions) {
        filterTableData__ProgramsOfStudy = programsOfStudyOptions.map((type) => ({
            id: type.id,
            name: type.name.charAt(0).toUpperCase() + type.name.slice(1),
        }));
    }

    if (certificateAwardsReport) {
        filterTableData__CertificateAwardsReport = certificateAwardsReport.table_data.map((certificate) => ({
            id: certificate.id,
            full_name: certificate.full_name,
            email: certificate.email,
            school_name: certificate.school_name,
            course_name: certificate.course_name,
            status: certificate.status,
            completed_at: certificate.completed_at ? getDateString(certificate.completed_at) : '-',
        }));
    }

    const onExportCertificateAwardsReport = () => {
        exportCertificateAwardsReport({
            payload: {
                filterQuery: filterQuery.toString(),
            }
        });
    }

    return (
        <ReportLayout>
            <div className='immersion_admin__careers_report'>
                <div className='immersion_admin__careers_report__header'>
                    <div className='immersion_admin__careers_report__header__content'>
                        <h2 className='immersion_admin__careers_report__header__content__title'>Certificate Awards Report</h2>
                        <p className='immersion_admin__careers_report__header__content__description'>
                            This slide gives an overview of certificate awards completed by students.
                        </p>
                    </div>
                    <div className='immersion_admin__careers_report__header__actions'>
                        <button className='immersion_admin__btn__primary immersion_admin__careers_report__header__actions__button' onClick={onExportCertificateAwardsReport}>Export CSV</button>
                    </div>
                </div>

                <div className='immersion_admin__careers_report__chart_body'>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <FilterTable
                            columns={filterTableDataColumns__Schools}
                            data={filterTableData__Schools}
                            enableFilters={true}
                            filters={schoolFilters}
                            setFilters={setSchoolFilters}
                        />
                        <FilterTable
                            columns={filterTableDataColumns__Campuses}
                            data={filterTableData__Campuses}
                            enableFilters={true}
                            filters={campusFilters}
                            setFilters={setCampusFilters}
                        />
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <ReportDatePicker
                                required={true}
                                startDate={startDate}
                                setStartDate={setStartDate}
                                endDate={endDate}
                                setEndDate={setEndDate}
                            />
                            <div className='immersion_admin__careers_report__chart__row'>
                                <ReportTablet
                                    title={certificateAwardsReport?.total_enrollments?.toString() ?? "0"}
                                    description='Total Enrollments'
                                    color={'green'}
                                />
                                <ReportTablet
                                    title={certificateAwardsReport?.total_courses_completed?.toString() ?? "0"}
                                    description='Total Courses Completed'
                                    color={'green'}
                                />
                                <ReportTablet
                                    title={`${certificateAwardsReport?.completion_rate?.toFixed(1) ?? "0"}%`}
                                    description='Completion Rate'
                                    color={'green'}
                                />
                            </div>
                        </div>
                    </div>
                    <div className='immersion_admin__careers_report__chart__row'>
                        <div className='immersion_admin__careers_report__chart__row immersion_admin__careers_report__chart__row__vertical'>
                            <FilterTable
                                columns={filterTableDataColumns__ProgramsOfStudy}
                                data={filterTableData__ProgramsOfStudy}
                                enableFilters={true}
                                filters={programsOfStudyFilters}
                                setFilters={setProgramsOfStudyFilters}
                            />
                            <FilterTable
                                columns={filterTableDataColumns__CertificateAwardsReport}
                                data={filterTableData__CertificateAwardsReport}
                                enableFilters={false}
                                filters={[]}
                                setFilters={() => { }}
                            />
                        </div>
                        {certificateAwardsReport?.status_chart_data.length ? (
                            <BarChart
                                width={600}
                                height={318}
                                data={certificateAwardsReport?.status_chart_data ?? []}
                                xLabel="Status"
                                yLabel="No. of Certificates Awarded"
                                xInterval={0}
                            />
                        ) : (
                            <div className='immersion_admin__careers_report__chart__no_data'>
                                No certificate awards completed
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </ReportLayout>
    )
}

export default CertificateAwards;